<template>
  <v-app>
    <!-- Signed in (and not an auth page): the shell, the desktop app in a
         browser (SIGNED_IN_PLAN section 3). -->
    <AppShell v-if="useShell" />

    <!-- Signed out, and the auth pages: the guest frame. Welcome, the auth
         forms and an invitation are the whole window (their boards have no
         bar); every other page keeps it. -->
    <template v-else>
      <v-app-bar v-if="!bareForGuest" app class="modern-app-bar">
        <v-container fluid class="pa-0">
          <div class="app-bar-content">
            <div class="app-bar-left">
              <router-link to="/" class="logo-link">
                <v-img
                  contain
                  width="40"
                  height="40"
                  alt="cloudloop"
                  :src="require('./assets/cloudloop-logo-64.png')"
                />
              </router-link>
            </div>
            <div class="app-bar-center d-none d-md-flex"></div>
            <div class="app-bar-right">
              <div class="nav-actions">
                <!-- Signed out: Log in (tinted) and Create account (primary).
                     From a page like a shared session they come back here
                     afterwards. -->
                <template v-if="!loggedInUser">
                  <router-link :to="guestLink('/login')" class="cl-btn guest-btn">
                    Log in
                  </router-link>
                  <router-link
                    :to="guestLink('/sign-up')"
                    class="cl-btn cl-btn--primary guest-btn"
                  >
                    Create account
                  </router-link>
                </template>
              </div>
            </div>
          </div>
        </v-container>
      </v-app-bar>

      <v-main :class="themeClasses">
        <!-- Never a page that needs an account: signing out leaves it
             before this frame could show it. -->
        <router-view v-if="$route.meta.requiresAuth === false" />

        <!-- Two groups that never break inside: one line when it fits, else
             the groups stack, so no "·" is left at a line's end. The DMCA
             page has one name: the link, its heading and its tab title. -->
        <footer class="site-footer">
          <span class="footer-group">
            <router-link to="/about">About</router-link>
            <span class="footer-sep" aria-hidden="true">·</span>
            <router-link to="/terms">Terms</router-link>
            <span class="footer-sep" aria-hidden="true">·</span>
            <router-link to="/privacy">Privacy</router-link>
          </span>
          <span class="footer-sep footer-sep--between" aria-hidden="true">·</span>
          <span class="footer-group">
            <router-link to="/refunds">Refunds</router-link>
            <span class="footer-sep" aria-hidden="true">·</span>
            <router-link to="/dmca">Copyright &amp; DMCA policy</router-link>
          </span>
        </footer>

        <!-- A public session's Play all and the pack page keep their bar. -->
        <PaneFoot lazy />
      </v-main>
    </template>

    <NewSessionDialog />
    <ToastHost />
    <!-- Audio dropped anywhere: into your samples (or the open session). -->
    <PageDrop />
    <ConfirmHost />

    <!-- A newer deploy is live: offer a one-tap refresh instead of the manual
         hard-reload the fixed-name bundles would otherwise need. -->
    <v-snackbar
      :model-value="updateAvailable"
      :timeout="-1"
      location="top"
      color="var(--cl-toast)"
      class="update-snackbar"
    >
      <v-icon size="18" class="mr-2" color="var(--cl-accent-text)">mdi-rocket-launch-outline</v-icon>
      A new version of cloudloop is available.
      <template #actions>
        <v-btn variant="text" color="var(--cl-accent-text)" @click="refreshApp">Refresh</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<style scoped>
/* The page's own ground (cl-bg), with room under it for the player bar
   only while one shows (--cl-player-h, set by PlayerBar). */
.site-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  padding: 1.25rem 1rem calc(1.25rem + var(--cl-player-h, 0px));
  font-size: 0.8rem;
  background: var(--cl-bg);
}

.site-footer a {
  color: var(--text-subtle);
  text-decoration: none;
}

.site-footer a:hover {
  color: var(--text-secondary);
  text-decoration: underline;
}

.footer-sep {
  color: var(--text-subtle);
  opacity: 0.5;
}

.footer-group {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  white-space: nowrap;
}

/* Too narrow for one line (the whole row is about 380 px): the second
   group goes under the first, and the separator between them goes. */
@media (max-width: 559px) {
  .site-footer {
    flex-direction: column;
    gap: 0.5rem;
  }
  .footer-sep--between {
    display: none;
  }
}

/* Dark theme styles */
.main-content.theme--dark {
  background: var(--cl-cell);
  min-height: 100vh;
  transition: background-color 0.3s ease;
}

/* Light theme styles */
.main-content.theme--light {
  background: #f5f5f5;
  min-height: 100vh;
  transition: background-color 0.3s ease;
}

.search-dropdown {
  position: fixed;
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1500;
  width: 100%;
  max-width: 600px;
  pointer-events: auto;
}

.search-results-card {
  margin: 8px;
  border-radius: 16px;
  overflow: hidden;
  max-height: 70vh;
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
}

.theme--dark .search-results-card {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.theme--light .search-results-card {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.search-results-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 16px;
}

.search-result-item {
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
}

.search-result-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  gap: 12px;
  font-size: 1.1rem;
}

.theme--dark .no-results {
  color: rgba(255, 255, 255, 0.7);
}

.theme--light .no-results {
  color: rgba(0, 0, 0, 0.6);
}

.single-result-header {
  padding: 16px 20px 8px 20px;
}

.theme--dark .single-result-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.theme--light .single-result-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.result-header {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.theme--dark .result-header {
  color: white;
}

.theme--light .result-header {
  color: #2c3e50;
}

.search-tabs {
}

.theme--dark .search-tabs {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.theme--light .search-tabs {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.search-tab {
  font-weight: 500;
  text-transform: none;
  transition: all 0.3s ease;
}

.theme--dark .search-tab {
  color: rgba(255, 255, 255, 0.8) !important;
}

.theme--light .search-tab {
  color: rgba(0, 0, 0, 0.7) !important;
}

.theme--dark .search-tab:hover {
  color: white !important;
}

.theme--light .search-tab:hover {
  color: #2c3e50 !important;
}

.theme--dark .search-tab.v-tab--active {
  color: white !important;
}

.theme--light .search-tab.v-tab--active {
  color: #2c3e50 !important;
}

.more-results {
  text-align: center;
  padding: 16px 12px;
  font-style: italic;
  margin-top: 8px;
  font-size: 0.9rem;
}

.theme--dark .more-results {
  color: rgba(255, 255, 255, 0.6);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.theme--light .more-results {
  color: rgba(0, 0, 0, 0.5);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.actionBarButton {
  margin-right: 15px;
}

/* Mobile Header Styles */
.mobile-header {
  padding: 0 12px !important;
}

.logo-link {
  margin-right: 12px;
}

.search-field {
  flex: 1;
  max-width: 600px !important;
  margin: 0 16px;
}

.search-field :deep(.v-input__slot) {
  border-radius: 24px !important;
  transition: all 0.3s ease;
}

.theme--dark .search-field :deep(.v-input__slot) {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.theme--light .search-field :deep(.v-input__slot) {
  background: white !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
}

.theme--dark .search-field :deep(.v-input__slot:hover) {
  background: rgba(255, 255, 255, 0.15) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.theme--light .search-field :deep(.v-input__slot:hover) {
  border-color: rgba(0, 0, 0, 0.3) !important;
}

.theme--dark .search-field :deep(input) {
  color: white !important;
}

.theme--light .search-field :deep(input) {
  color: #2c3e50 !important;
}

.theme--dark .search-field :deep(input::placeholder) {
  color: rgba(255, 255, 255, 0.6) !important;
}

.theme--light .search-field :deep(input::placeholder) {
  color: rgba(0, 0, 0, 0.5) !important;
}

.theme--dark .search-field :deep(.v-icon) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.theme--light .search-field :deep(.v-icon) {
  color: rgba(0, 0, 0, 0.6) !important;
}

.user-avatar {
  margin-left: 8px;
}

.new-session-btn {
  background: var(--accent-soft) !important;
  border: 1px solid var(--accent-border) !important;
  color: var(--accent) !important;
  font-weight: 500 !important;
  border-radius: var(--radius-md) !important;
  padding: 0 0.85rem !important;
  height: 32px !important;
  font-size: var(--font-sm) !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  min-width: 0 !important;
}

.new-session-btn:hover {
  background: rgba(168, 85, 247, 0.22) !important;
  border-color: var(--accent) !important;
  color: var(--accent-hover) !important;
}

/* App Bar */
.modern-app-bar {
  background: var(--bg-elevated) !important;
  backdrop-filter: none !important;
  border-bottom: 1px solid var(--border-subtle) !important;
  padding: 0 1rem !important;
  box-shadow: none !important;
}

.search-field-modern {
  background: var(--bg-soft) !important;
  border-radius: var(--radius-md) !important;
  max-width: 420px;
  width: 420px;
}

.search-field-modern :deep(.v-field) {
  background: var(--bg-soft) !important;
  box-shadow: none !important;
  border-radius: var(--radius-md) !important;
  border: 1px solid var(--border-subtle) !important;
}

.search-field-modern :deep(.v-field:hover),
.search-field-modern :deep(.v-field--focused) {
  border-color: var(--border-default) !important;
}

.search-field-modern :deep(.v-field__input) {
  color: var(--text-primary) !important;
  padding: 0 0.75rem !important;
  font-size: 0.875rem !important;
  min-height: 36px !important;
}

.search-field-modern :deep(.v-field__input::placeholder) {
  color: var(--text-muted) !important;
}

.search-field-modern :deep(.v-field__prepend-inner) {
  color: var(--text-muted) !important;
}

.search-field-modern :deep(.v-field__outline) {
  display: none !important;
}

.nav-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.nav-btn {
  background: var(--bg-soft) !important;
  border: 1px solid var(--border-subtle) !important;
  color: var(--text-secondary) !important;
  border-radius: var(--radius-md) !important;
  min-width: 36px !important;
  width: 36px !important;
  height: 36px !important;
  transition: background var(--transition-fast), border-color var(--transition-fast);
}

.nav-btn:hover {
  background: var(--bg-soft-hover) !important;
  border-color: var(--border-default) !important;
  color: var(--text-primary) !important;
}

.nav-btn .v-icon {
  font-size: 18px !important;
}

.profile-btn {
  background: var(--surface-2) !important;
  border: 1px solid var(--border-subtle) !important;
  color: var(--text-primary) !important;
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  cursor: pointer;
  transition: background var(--transition-fast), border-color var(--transition-fast);
}

.profile-btn:hover {
  background: var(--surface-3) !important;
  border-color: var(--border-default) !important;
}

.profile-initial {
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.8125rem;
}

.username-display {
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 0.8125rem;
  letter-spacing: 0;
  text-transform: none;
  margin: 0 0.5rem;
  white-space: nowrap;
}

.logo-link {
  text-decoration: none;
  display: flex;
  align-items: center;
}

.upload-indicator {
  display: flex;
  align-items: center;
  margin: 0 6px;
  cursor: default;
}

.status-icon {
  font-size: 20px !important;
}

.status-icon.complete {
  color: var(--cl-live) !important;
}

.status-icon.error {
  color: var(--cl-destructive) !important;
}

.status-icon.adding {
  color: var(--cl-label-2) !important;
}

.upload-details {
  min-width: 260px;
  max-width: 340px;
  padding: 0.5rem 0.75rem;
  background: var(--cl-sheet) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.upload-details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  padding-bottom: 0.4rem;
  margin-bottom: 0.4rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.upload-details-close-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: var(--radius-sm);
  transition: color var(--transition-fast), background var(--transition-fast);
}

.upload-details-close-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.upload-details-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0;
  font-size: 0.75rem;
}

.upload-file-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.upload-file-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.75rem;
}

.upload-mini-progress {
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.upload-mini-progress-fill {
  height: 100%;
  background: var(--accent);
  transition: width 0.2s ease;
}

.upload-mini-progress-fill.is-indeterminate {
  animation: mini-upload-shimmer 1.5s infinite linear;
  background: var(--cl-accent);
  background-size: 200% 100%;
}

@keyframes mini-upload-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.upload-file-state {
  color: rgba(255, 255, 255, 0.55);
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

/* Guest bar: Log in and Create account (kit buttons on router links). */
.guest-btn {
  text-decoration: none;
}

@media (max-width: 768px) {
  .mobile-header {
    min-height: 56px !important;
    padding: 0 8px !important;
  }

  .logo-link {
    margin-right: 8px;
  }

  .search-field {
    margin: 0 8px;
    max-width: none !important;
    flex: 1;
  }

  .search-field :deep(.v-input__control) {
    min-height: 36px !important;
  }

  .search-field :deep(.v-input__slot) {
    padding: 0 12px !important;
  }

  .search-field :deep(input) {
    font-size: 14px !important;
  }

  .user-avatar {
    margin-left: 4px;
  }

  .search-dropdown {
    max-width: 95vw;
    left: 2.5vw;
    transform: none;
    top: 56px !important;
  }

  .search-results-card {
    margin: 4px;
  }

  .search-results-list {
    max-height: 300px;
    padding: 4px;
  }

  .search-result-item {
    margin-bottom: 4px;
  }
}

/* App Bar Layout */
.app-bar-content {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
  padding: 0 16px;
}

.app-bar-left {
  flex: 0 0 auto;
}

.app-bar-center {
  flex: 1;
  display: flex;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto;
}

.app-bar-right {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  /* Keep actions pinned to the right even when the center search is hidden
     (mobile), where there's otherwise no flexible element to fill the gap. */
  margin-left: auto;
}

/* Mobile Responsive Styles */
@media (max-width: 959px) {
  .app-bar-content {
    padding: 0 12px;
    gap: 12px;
  }
  
  .nav-actions {
    gap: 4px;
  }
  
  .new-session-btn {
    min-width: 40px !important;
    padding: 0 8px !important;
    font-size: 13px !important;
  }
  
  .username-display {
    font-size: 14px !important;
    margin-right: 8px !important;
  }
  
  .profile-btn {
    margin-left: 4px !important;
  }
  
  .mobile-search-btn {
    margin-right: 8px;
  }
}

@media (max-width: 599px) {
  .app-bar-content {
    padding: 0 8px;
    gap: 8px;
  }
}

/* Mobile Search Dialog Styles */
.mobile-search-dialog .v-dialog {
  margin: 0 !important;
}

/* Opaque: this is a fullscreen dialog with no scrim, so a see-through card
   shows the page underneath. (--v-background-base was a Vuetify 2 variable and
   resolved to nothing here.) */
.mobile-search-card {
  background: var(--bg-app) !important;
  box-shadow: none !important;
  min-height: 100vh;
}

.mobile-search-toolbar {
  padding: 8px 8px 8px 16px !important;
  box-shadow: none !important;
  background: var(--bg-app) !important;
}

.search-field-mobile {
  flex: 1;
}

.mobile-search-results {
  padding: 8px 16px 16px;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
}

.mobile-search-results .search-result-item {
  margin-bottom: 8px;
}

.mobile-search-results .no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 2.5rem 1rem;
  color: var(--text-muted);
}

.mobile-search-results .search-results {
  padding: 0;
}

</style>

<script>
import NewSessionDialog from "@/components/NewSessionDialog";
import AppShell from "@/shell/AppShell.vue";
import PaneFoot from "@/shell/PaneFoot.vue";
import ToastHost from "@/shell/ToastHost.vue";
import PageDrop from "@/shell/PageDrop.vue";
import ConfirmHost from "@/shell/ConfirmHost.vue";
import { shellState } from "@/shell/shellState.js";
import { syncWebPush, takeSessionExpired } from "@/shell/account.js";
import { trackViewport } from "@/shell/sizeClass.js";
import { afterSignOut } from "@/router/redirects.js";
import { ACTIVE_UPLOAD_STATES as ACTIVE_STATES } from "@/shell/shellState.js";
import {
  updateState,
  startUpdateChecks,
  applyUpdate,
} from "@/services/updateCheck";
import * as webPush from "@/services/webPush";
import { refreshSocketAuth } from "@/services/liveSocket";
import { guestChrome } from "@/kit/chrome.js";
import nowPlaying from "@/audio/nowPlaying.js";

export default {
  name: "App",
  components: { AppShell, PaneFoot, ToastHost, ConfirmHost, NewSessionDialog, PageDrop },
  data: () => ({
    mediaQuery: null,
    uploadCleanupTimers: {},
  }),
  computed: {
    updateAvailable() {
      return updateState.available;
    },
    // Signed in, every page but the auth forms sits in the shell (SI-1).
    // Home is the Welcome board: signed in it only shows between logging in
    // on it and LoginForm's push to the landing (the guard sends / to
    // Sessions: Mine otherwise), so it keeps the guest frame rather than draw
    // Welcome's log in form inside the sidebar.
    useShell() {
      return !!this.loggedInUser && !this.$route.meta.guestFrame && this.$route.name !== "Home";
    },
    // The auth pages have no top bar for guests, unless the page shows a
    // status page (a dead invitation) and asks for it (kit/chrome.js).
    bareForGuest() {
      return (
        !this.loggedInUser &&
        !!this.$route.meta.bareForGuest &&
        guestChrome.topBarPath !== this.$route.path
      );
    },
    loggedInUser() {
      return this.$store.state.loggedInUser;
    },
    themeClasses() {
      return {
        "theme--dark": true,
        "theme--light": false,
        "main-content": true,
      };
    },
    uploadQueue() {
      return this.$store.state.files;
    },
    activeUploads() {
      return this.uploadQueue.filter((file) => ACTIVE_STATES.includes(file.status));
    },
  },
  methods: {
    refreshApp() {
      applyUpdate();
    },
    // Log in / Create account from the guest bar: return to this page
    // afterwards, unless it's Home or one of the auth pages.
    guestLink(path) {
      const here = this.$route;
      if (!here || here.path === "/" || here.meta.bareForGuest) return path;
      return { path, query: { redirect: here.fullPath } };
    },
    initializeTheme() {
      if (window.matchMedia) {
        this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        this.updateTheme(this.mediaQuery.matches);
        this.mediaQuery.addEventListener("change", this.handleThemeChange);
      }
    },
    handleThemeChange(e) {
      this.updateTheme(e.matches);
    },
    updateTheme(isDark) {
      this.$vuetify.theme.dark = isDark;
      localStorage.setItem("theme-preference", isDark ? "dark" : "light");
    },
  },
  watch: {
    // Signed in (or restored on load): re-register this browser's push token
    // so one the server pruned, or FCM rotated, comes back without a prompt.
    loggedInUser: {
      handler(user, previous) {
        if (user) webPush.resync();
        // Signing out or deleting the account elsewhere may have cleared
        // the subscription; keep the Settings toggle honest.
        syncWebPush();
        // Signed in, out, or as someone else: the socket must re-handshake
        // with the new token (not on first load: it connected with it).
        if (previous !== undefined && user?.username !== previous?.username) {
          refreshSocketAuth();
        }
        // Signed out, or in as someone else: nothing the last account
        // played keeps sounding or stays on the bar (its items carry that
        // account's tokens).
        if (previous && user?.username !== previous.username) nowPlaying.stopAll();
        // Signed out from anywhere (Settings, an expired token, another tab,
        // a deleted account): leave a page that needs an account, and drop
        // what the last account loaded, rather than keep showing it.
        if (!user && previous) {
          this.$store.commit("clearUserData");
          // Welcome, or Log in and back when the session expired (L11).
          const to = afterSignOut(this.$route, { expired: takeSessionExpired() });
          if (to) this.$router.replace(to).catch(() => {});
        }
      },
      immediate: true,
    },
    // Uploads starting open the queue's menu, once its ring button exists
    // (UploadQueueButton, SI-19). On the session page each upload is its
    // own row, so the menu would only repeat them.
    activeUploads: {
      handler(active, prev) {
        if (active.length > 0) {
          if (!shellState.uploads.open && this.$route?.name !== "Session") {
            this.$nextTick(() =>
              requestAnimationFrame(() => {
                if (this.activeUploads.length > 0 && this.$route?.name !== "Session") {
                  shellState.uploads.open = true;
                }
              }),
            );
          }
        } else if (prev && prev.length > 0 && active.length === 0) {
          setTimeout(() => {
            if (this.activeUploads.length === 0) shellState.uploads.open = false;
          }, 3500);
        }
      },
      deep: true,
      immediate: true,
    },
    // Finished files linger briefly so the user sees the outcome, then get
    // cleared so the indicator doesn't accumulate icons across sessions.
    uploadQueue: {
      handler(files) {
        files.forEach((file) => {
          if (this.uploadCleanupTimers[file.uuid]) return;
          if (file.status !== "complete" && file.status !== "error") return;
          const delay = file.status === "complete" ? 4000 : 15000;
          this.uploadCleanupTimers[file.uuid] = setTimeout(() => {
            delete this.uploadCleanupTimers[file.uuid];
            this.$store.commit("removeFile", file.uuid);
          }, delay);
        });
      },
      deep: true,
    },
  },

  mounted() {
    // The window's size for every page, the signed-out ones too (L10;
    // AppShell's own tracking only runs signed in).
    this.stopViewport = trackViewport();
    this.initializeTheme();
    startUpdateChecks();
    webPush.listenForNotificationClicks(this.$router);
  },

  beforeUnmount() {
    if (this.stopViewport) this.stopViewport();
    Object.values(this.uploadCleanupTimers).forEach(clearTimeout);
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.handleThemeChange);
    }
  },
};
</script>
